import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Switch } from '@/app/components/ui/switch';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { usePlatformCustomGearModelGroups, usePromoteGearModel } from '@/hooks/use-platform';

const ASSET_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

export function GearModelDuplicatesPage() {
  const [appearsIn2Plus, setAppearsIn2Plus] = useState(true);
  const [assetCategory, setAssetCategory] = useState('all');
  const { data: groups = [], isLoading, isError, refetch } = usePlatformCustomGearModelGroups({
    appears_in_2plus: appearsIn2Plus,
    asset_category: assetCategory === 'all' ? undefined : assetCategory,
  });
  const promoteMutation = usePromoteGearModel();

  const handlePromote = async (group: (typeof groups)[number]) => {
    try {
      const result = await promoteMutation.mutateAsync({
        normalized_key: group.normalized_key,
      });
      toast.success(
        `Promoted ${group.manufacturer_name} ${group.name} to catalog. ${result.assets_relinked} asset(s) relinked.`
      );
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Promotion failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button asChild variant="ghost" size="icon">
              <Link to="/platform/gear-models">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Custom Gear Model Review</h1>
          </div>
          <p className="text-gray-600 mt-1">
            Org-submitted gear models grouped by exact normalized match. Promote likely-real items to the global catalog.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex items-center gap-2">
            <Switch
              id="appears-in-2plus"
              checked={appearsIn2Plus}
              onCheckedChange={setAppearsIn2Plus}
            />
            <Label htmlFor="appears-in-2plus">Only show entries appearing in 2+ orgs</Label>
          </div>
          <Select value={assetCategory} onValueChange={setAssetCategory}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              {ASSET_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className="p-6 text-center text-red-600">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
          Failed to load custom gear models.
        </div>
      ) : groups.length === 0 ? (
        <div className="p-12 text-center text-gray-600">
          <CheckCircle2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="font-medium">No custom gear models to review</p>
          <p className="text-sm text-gray-500 mt-1">
            {appearsIn2Plus
              ? 'No models appear in 2 or more organizations yet.'
              : 'No org-scoped custom gear models have been submitted.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.normalized_key}>
              <CardHeader className="pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">
                      {group.manufacturer_name} {group.name}
                    </CardTitle>
                    <CardDescription>
                      {group.model_number || 'No model number'} · {group.asset_category_display}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={group.org_count >= 2 ? 'default' : 'secondary'}>
                      {group.org_count} org{group.org_count === 1 ? '' : 's'}
                    </Badge>
                    <Badge variant="outline">{group.instance_count} submission{group.instance_count === 1 ? '' : 's'}</Badge>
                    <Button
                      size="sm"
                      onClick={() => handlePromote(group)}
                      disabled={promoteMutation.isPending}
                    >
                      Promote to Catalog
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600 mb-2">Submitted by:</div>
                <div className="flex flex-wrap gap-2">
                  {group.organizations.map((org) => (
                    <div
                      key={org.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-background text-sm"
                    >
                      <Building2 className="h-3.5 w-3.5 text-gray-500" />
                      {org.name}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
